import moment from 'moment'
import Select from '@atlaskit/select'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { Drawer } from 'antd'

import { normalize, notifOps, usePet } from '../../modules'

import Datepicker from '../reusable/datePicker'
import SelectLabel from '../reusable/selectLabel'
import Confirm from '../reusable/confirm'

const claimOutstanding = claim => (claim?.financials?.totalAmount ?? 0) - (claim?.paidAmount ?? 0)
const depositAccountTypes = ['Cash', 'Bank']

const RecordShaRemittance = ({ visible, claims = [], insurer, onClose, onPerform }) => {
    const [date, setDate] = useState(moment())
    const [ref, setRef] = useState('')
    const [account, setAccount] = useState()
    const [method, setMethod] = useState()
    const [amounts, setAmounts] = useState(() => claims.reduce((obj, claim) => ({ ...obj, [claim._id]: claimOutstanding(claim) }), {}))

    const [accounts, setAccounts] = useState([])
    const [methods, setMethods] = useState([])

    const { enqueueSnackbar } = useSnackbar()

    usePet('accounts', { string: '', limit: 100, types: depositAccountTypes }, { notify: true, callback: ({ data }) => {
        let obj = {}

        data?.accounts?.forEach(acc => {
            if (!obj[acc.type]) obj[acc.type] = []
            obj[acc.type].push({ label: acc.name, value: acc._id })
        })

        let final = []
        for (let typ in obj) {
            final.push({ label: typ, options: obj[typ] })
        }

        setAccounts(final)
    } })

    usePet('paymentMethodNames', { type: 'regular' }, { notify: true, callback: ({ data }) => setMethods(data) })

    const close = () => {
        onClose()
    }

    const onComplete = (_, notif, ops) => {
        onPerform()
        notif('Remittance has been recorded', ops('success'))
        close()
    }

    const total = Object.values(amounts).reduce((sum, val) => sum + (Number(val) || 0), 0)

    const [{ loading }, execute] = usePet('recordShaRemittance', {
        insurer: insurer?._id,
        allocations: claims.map(claim => ({ claim: claim._id, amount: Number(amounts[claim._id]) || 0 })),
        payments: [
            {
                method: method?.value,
                account: account?.value,
                amount: total,
                ref,
            }
        ],
        date,
    }, { notify: true, callback: onComplete, manual: true })

    const setClaimAmount = (claimId, value) => {
        const outstanding = claimOutstanding(claims.find(claim => claim._id === claimId))

        if (Number(value) > outstanding) {
            enqueueSnackbar(`Amount cannot exceed this claim's outstanding balance of Ksh. ${normalize(outstanding)}`, notifOps('warning'))
            return
        }

        setAmounts({ ...amounts, [claimId]: value })
    }

    const enableAmounts = total > 0 && claims.every(claim => Number(amounts[claim._id]) >= 0 && Number(amounts[claim._id]) <= claimOutstanding(claim))

    const recordRemittance = () => {
        if (!method) enqueueSnackbar('Please indicate the payment method', notifOps('warning'))
        if (!account) enqueueSnackbar('Please indicate the destination account', notifOps('warning'))
        if (!ref?.trim()) enqueueSnackbar('Please indicate the remittance advice reference number', notifOps('warning'))
        if (!enableAmounts) enqueueSnackbar('Please enter valid amounts for every claim', notifOps('warning'))

        if (method && account && ref?.trim() && enableAmounts)
            Confirm({
                title: `Record remittance of Ksh. ${normalize(total)}?`,
                message: `This will reduce ${insurer?.name}'s outstanding balance by Ksh. ${normalize(total)} across ${claims.length} claim(s), deposited into ${account?.label}`,
                okText: 'Record Remittance',
                onOk: execute
            })
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={ visible }
            onClose={ close }
            footer={
                <div className="w-full text-right">
                    <button disabled={ loading } onClick={ recordRemittance } className="text-white hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save</button>
                </div>
            }
            width="40%">
            <p className="font-extrabold text-3xl mb-0">Record SHA Remittance</p>
            <p className="text-sm italic mb-0 text-gray-700">{ insurer?.name ?? 'SHA' } — Allocated: <span className="font-semibold">Ksh. { normalize(total) }</span></p>
            <div className="flex flex-wrap mt-3">
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Date
                    </label>
                    <Datepicker value={ date } onChange={ setDate } />
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Deposited Into
                    </label>
                    <Select
                        className="mb-3"
                        value={ account }
                        onChange={ setAccount }
                        formatGroupLabel={ SelectLabel }
                        options={ accounts }
                        placeholder="Select account" />
                    <p className="text-gray-500 text-xs italic">Only cash and bank destination accounts are shown here so remittances land in deposit-ready accounts.</p>
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Payment Method
                    </label>
                    <Select
                        className="mb-3"
                        value={ method }
                        onChange={ setMethod }
                        options={ methods.map(method => ({ label: method, value: method })) }
                        placeholder="Select payment method" />
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Remittance Advice Ref
                    </label>
                    <input
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        type="text"
                        value={ ref }
                        onChange={ e => setRef(e.target.value) }
                        placeholder="XXXXXX" />
                    <p className="text-gray-500 text-xs italic">Required - the unique reference on the SHA remittance advice, used to detect and safely skip a duplicate submission</p>
                </div>

                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Per-Claim Amounts
                    </label>
                    <div className="border border-gray-200 rounded-sm divide-y divide-gray-100">
                        {
                            claims.map(claim =>
                                <div key={ claim._id } className="flex items-center justify-between px-3 py-2 gap-3">
                                    <div className="min-w-0">
                                        <p className="mb-0 text-sm truncate">{ claim?.patientData?.name ?? claim?.externalClaimId ?? claim._id }</p>
                                        <p className="mb-0 text-xs text-gray-500">Outstanding: Ksh. { normalize(claimOutstanding(claim)) }</p>
                                    </div>
                                    <input
                                        className="appearance-none block w-32 bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white"
                                        type="number"
                                        value={ amounts[claim._id] }
                                        onChange={ e => setClaimAmount(claim._id, e.target.value) }
                                        placeholder="0" />
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        </Drawer>
    )
}

export default RecordShaRemittance
